import { toast } from "../../common_components/Toast/ToastUtils";
import XLSX from "xlsx-js-style";
import { getStatusLabel, getTypeLabel, formatDate } from "./Component/shared";
const titleStyle = {
    font: { bold: true, sz: 18, color: { rgb: "181D27" } },
    alignment: { horizontal: "center", vertical: "center" },
};
const headerStyle = {
    font: { bold: true, sz: 11, color: { rgb: "374151" } },
    fill: { fgColor: { rgb: "E5E7EB" } },
    alignment: { horizontal: "center", vertical: "center" },
    border: {
        top: { style: "thin", color: { rgb: "D1D5DB" } },
        bottom: { style: "thin", color: { rgb: "D1D5DB" } },
    },
};
const defaultCellStyle = {
    alignment: { horizontal: "left", vertical: "center", wrapText: true },
    border: {
        top: { style: "thin", color: { rgb: "E5E7EB" } },
        bottom: { style: "thin", color: { rgb: "E5E7EB" } },
        left: { style: "thin", color: { rgb: "E5E7EB" } },
        right: { style: "thin", color: { rgb: "E5E7EB" } },
    },
};
const withDash = (value) => value === null || value === undefined || String(value).trim().length === 0 ? "-" : String(value).trim();
const exportColumns = [
    { id: "client", label: "Client", getter: (row) => withDash(row.client?.name) },
    { id: "am", label: "AM", getter: (row) => withDash((row.am || []).map((a) => a.name).join(", ")) },
    { id: "os", label: "OS", getter: (row) => withDash(row.os?.name) },
    { id: "type", label: "Type", getter: (row) => withDash(getTypeLabel(row.type)) },
    { id: "tracker", label: "Tracker", getter: (row) => withDash(row.tracker_name) },
    { id: "launch_date", label: "Launch Date", getter: (row) => withDash(formatDate(row.launch_date)) },
    { id: "end_date", label: "End Date", getter: (row) => withDash(formatDate(row.end_date)) },
    { id: "status", label: "Status", getter: (row) => withDash(getStatusLabel(row.status)) },
    { id: "keywords", label: "Keywords", getter: (row) => withDash(row.keywords) },
    { id: "pitch", label: "Pitch", getter: (row) => withDash(row.pitch) },
    { id: "tracker_link", label: "Tracker", getter: (row) => withDash(row.tracker) },
    { id: "c_total", label: "Total", getter: (row) => withDash(row.contact_info?.total) },
    { id: "c_enrolled", label: "Enrolled", getter: (row) => withDash(row.contact_info?.enrolled) },
    { id: "c_unenrolled", label: "Unenrolled", getter: (row) => withDash(row.contact_info?.unenrolled) },
    { id: "c_bounced", label: "Bounced", getter: (row) => withDash(row.contact_info?.bounced) },
    { id: "c_sent", label: "Sent", getter: (row) => withDash(row.contact_info?.sent) },
    { id: "l_total", label: "Total", getter: (row) => withDash(row.lead_info?.total) },
    { id: "l_positive", label: "Positive", getter: (row) => withDash(row.lead_info?.positive) },
    { id: "l_negative", label: "Negative", getter: (row) => withDash(row.lead_info?.negative) },
    { id: "l_neutral", label: "Neutral", getter: (row) => withDash(row.lead_info?.neutral) },
    { id: "post_mortem", label: "Post Mortem", getter: (row) => withDash(row.post_mortem) },
];
const getColumnWidths = (rows, headers, groupHeaders = []) => {
    return headers.map((header, index) => {
        const maxLength = Math.max(header.length, groupHeaders[index]?.length ?? 0, ...rows.map((row) => String(row[index] || "").length));
        return { wch: Math.min(40, Math.max(12, Math.ceil(maxLength * 1.2))) };
    });
};
const columnGroupLabels = {
    client: "Client",
    am: "AM",
    os: "OS",
    type: "Tracker Status",
    tracker: "Tracker Status",
    launch_date: "Tracker Status",
    end_date: "Tracker Status",
    status: "Tracker Status",
    keywords: "Tracker Info",
    pitch: "Tracker Info",
    tracker_link: "Tracker Info",
    c_total: "Contact Info",
    c_enrolled: "Contact Info",
    c_unenrolled: "Contact Info",
    c_bounced: "Contact Info",
    c_sent: "Contact Info",
    l_total: "Lead Info",
    l_positive: "Lead Info",
    l_negative: "Lead Info",
    l_neutral: "Lead Info",
    post_mortem: "Lead Info",
};
const groupHeaderStyle = {
    font: { bold: true, sz: 11, color: { rgb: "374151" } },
    alignment: { horizontal: "center", vertical: "center" },
    fill: { fgColor: { rgb: "F3F4F6" } },
    border: {
        top: { style: "thin", color: { rgb: "D1D5DB" } },
        bottom: { style: "thin", color: { rgb: "D1D5DB" } },
    },
};
export default function exportOutreachTracker(rows, hiddenColumns, fileName = "Outreach_Tracker") {
    if (!rows?.length) {
        toast.info("No data available to export");
        return;
    }
    const visibleColumns = exportColumns.filter((column) => !hiddenColumns?.has(column.id));
    if (!visibleColumns.length) {
        toast.error("No visible columns available for export.");
        return;
    }
    const headers = visibleColumns.map((column) => column.label);
    const groupHeaders = visibleColumns.map((column) => {
        const label = columnGroupLabels[column.id] ?? column.label;
        return label === column.label ? "" : label;
    });
    const dataRows = rows.map((row) => visibleColumns.map((column) => column.getter(row)));
    const titleRow = ["Outreach Tracker", ...Array(headers.length - 1).fill("")];
    const spacerRow = Array(headers.length).fill("");
    const sheetData = [titleRow, spacerRow, groupHeaders, headers, ...dataRows];
    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
    const merges = [{ s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } }];
    let start = 0;
    while (start < groupHeaders.length) {
        const currentGroup = groupHeaders[start];
        if (!currentGroup) {
            start += 1;
            continue;
        }
        let end = start + 1;
        while (end < groupHeaders.length && groupHeaders[end] === currentGroup) {
            end += 1;
        }
        if (end - start > 1) {
            merges.push({ s: { r: 2, c: start }, e: { r: 2, c: end - 1 } });
        }
        start = end;
    }
    worksheet["!merges"] = merges;
    if (worksheet["A1"])
        worksheet["A1"].s = titleStyle;
    for (let c = 0; c < headers.length; c += 1) {
        const groupCellRef = XLSX.utils.encode_cell({ r: 2, c });
        const headerCellRef = XLSX.utils.encode_cell({ r: 3, c });
        if (worksheet[groupCellRef])
            worksheet[groupCellRef].s = groupHeaderStyle;
        if (worksheet[headerCellRef])
            worksheet[headerCellRef].s = headerStyle;
    }
    const numRows = sheetData.length;
    for (let r = 4; r < numRows; r += 1) {
        for (let c = 0; c < headers.length; c += 1) {
            const cellRef = XLSX.utils.encode_cell({ r, c });
            if (worksheet[cellRef]) {
                worksheet[cellRef].s = { ...defaultCellStyle };
            }
        }
    }
    worksheet["!cols"] = getColumnWidths(dataRows, headers, groupHeaders);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Outreach Tracker");
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
}
